import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import { whatsappLink } from "@/data/site";
import { CATEGORIES, PRODUCTS } from "@/data/products";

export default function Products() {
  const [active, setActive] = useState("All");
  const list = useMemo(
    () => (active === "All" ? PRODUCTS : PRODUCTS.filter((p) => p.category === active)),
    [active]
  );

  return (
    <>
      <section className="pt-40 pb-16 bg-ink" data-testid="products-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="text-[11px] sm:text-xs uppercase tracking-overline text-gold mb-6">Our Products · 60+ Varieties</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-display text-5xl sm:text-7xl tracking-tight leading-[1.02] text-cream max-w-4xl">
              The signature<br /><em className="text-gold italic">collection.</em>
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-7 max-w-2xl text-base md:text-lg text-zinc-400 font-light leading-relaxed">
              A representative selection from our 60+ variety portfolio. Every product available in retail,
              family, bulk and export pack formats.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="pb-24 sm:pb-32 bg-ink" data-testid="products-catalogue">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-wrap gap-2.5 sticky top-[76px] z-30 bg-ink/90 backdrop-blur-md py-5 border-b border-white/10" data-testid="product-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`text-[11px] sm:text-xs uppercase tracking-[0.16em] px-5 py-2.5 rounded-full border transition-[background-color,color,border-color] duration-300 ${
                  active === c
                    ? "bg-gold text-ink border-gold font-semibold"
                    : "border-white/15 text-zinc-400 hover:border-gold/60 hover:text-gold"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <motion.div layout className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <AnimatePresence mode="popLayout">
              {list.map((p, i) => (
                <motion.div
                  key={p.id}
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.5, delay: (i % 4) * 0.05, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div
                    data-testid={`product-card-${p.id}`}
                    onMouseMove={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                      e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                    }}
                    className="group relative h-full bg-coal border border-white/10 rounded-sm overflow-hidden transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-2 hover:border-gold/50 hover:shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
                  >
                    <div className="spotlight-overlay pointer-events-none absolute inset-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative aspect-[4/3.4] overflow-hidden">
                      <img src={p.img} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-coal/90 via-transparent to-transparent" />
                      <span className={`absolute top-3 left-3 text-[9px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full ${p.brand === "Tasty Mint" ? "bg-emerald/90 text-emerald-100 border border-emerald-light/50" : "bg-maroon/90 text-red-100 border border-maroon-light/50"}`}>
                        {p.brand}
                      </span>
                    </div>
                    <div className="p-4 sm:p-5">
                      <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-gold">{p.category}</p>
                      <h3 className="mt-1.5 font-display text-base sm:text-lg text-cream leading-snug">{p.name}</h3>
                      <p className="mt-2 text-xs sm:text-sm text-zinc-500 font-light leading-relaxed line-clamp-2">{p.desc}</p>
                      <a
                        href={whatsappLink(`Namaste Waghe & Sons! I'm interested in "${p.name}" (${p.brand}). Please share pricing & pack sizes.`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`product-enquire-${p.id}`}
                        className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-gold border-b border-gold/40 pb-1 hover:gap-3.5 transition-[gap] duration-300"
                      >
                        <MessageCircle className="w-3.5 h-3.5" /> Enquire
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          <FadeUp className="mt-16 border border-gold/25 bg-coal rounded-sm p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-testid="products-bulk-note">
            <div>
              <h3 className="font-display text-2xl text-cream">Need the full 60+ variety catalogue?</h3>
              <p className="mt-2 text-sm text-zinc-400 font-light">Wholesale rate list, pack sizes and territory availability — straight from our sales desk.</p>
            </div>
            <a
              href={whatsappLink("Namaste! Please share the full Waghe Shree / Tasty Mint catalogue with wholesale rates.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="products-catalogue-whatsapp"
              className="shrink-0 inline-flex items-center gap-2.5 bg-gold text-ink text-sm font-semibold uppercase tracking-[0.14em] px-7 py-4 rounded-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(212,175,55,0.35)]"
            >
              <MessageCircle className="w-4 h-4" /> Get Catalogue
            </a>
          </FadeUp>
        </div>
      </section>
      <EditorialMarquee variant="dark" testId="products-marquee" />
    </>
  );
}
